<template>
    <div class="py-2 md:py-4 relative">
        <div class="w-full flex justify-end mb-4">
            <a
                href="#"
                @click.prevent="appStore.setShowAffiliateModal(true)"
                class="text-orange text-xs hover:text-orange-400 whitespace-nowrap"
            >
                Affiliate Disclosure
            </a>
        </div>
        <div
            class="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-5"
        >
            <div class="gap-2 flex flex-col">
                <h2 class="text-2xl font-semibold text-slate-800">
                    Your Trips
                </h2>
                <p class="text-slate-500 text-sm">
                    View a history of all your point redemptions for travel
                </p>
            </div>
            <div class="flex gap-4 mt-4 md:mt-0">
                <button
                    class="btn btn-orange !mt-0 !px-4 flex items-center gap-2"
                    @click="openTripModal()"
                >
                    <Icon icon="plus" />
                    Create trip
                </button>
            </div>
        </div>
        <div class="mb-6 md:mb-8">
            <div class="inline-flex items-center max-w-80 gap-4">
                <label
                    class="py-4 text-sm font-medium border-b-2 cursor-pointer transition-colors duration-300 relative z-10"
                    :class="{
                        ' border-orange-500 text-slate-800':
                            tripScheduled === 'upcoming',
                        'text-slate-600': tripScheduled === 'past',
                    }"
                >
                    <span>Upcoming</span>
                    <input
                        class="sr-only"
                        type="radio"
                        name="trip_scheduled"
                        value="upcoming"
                        v-model="tripScheduled"
                    />
                </label>
                <label
                    class="py-4 text-sm font-medium border-b-2 cursor-pointer transition-colors duration-300 relative z-10"
                    :class="{
                        ' border-orange-500 text-slate-800':
                            tripScheduled === 'past',
                        'text-slate-600': tripScheduled === 'upcoming',
                    }"
                >
                    <span>Past</span>
                    <input
                        class="sr-only"
                        type="radio"
                        name="trip_scheduled"
                        value="past"
                        v-model="tripScheduled"
                    />
                </label>
            </div>
            <hr class="border-t-2 border-slate-200 w-full relative -mt-[2px]" />
        </div>

        <div class="flex flex-col gap-4" v-if="tripScheduled === 'upcoming'">
            <TripCard
                v-for="trip in upcomingTrips"
                :key="trip.id"
                :trip="trip"
            />
            <div class="w-full pt-5">
                <Pagination
                    :current-page="upcomingTripsPage"
                    :total-pages="upcomingTripsPages"
                    :per-page="perPage"
                    :total-results="upcomingTripsTotal"
                    :max-visible-buttons="5"
                    @page-changed="handleUpcomingPageChange"
                    v-if="upcomingTrips.length > 0"
                />
            </div>
            <EmptyState
                v-if="upcomingTrips.length === 0 && showEmptyTripState"
                class="mb-10"
                :icon="'trip'"
                title="No upcoming trips yet"
                description="Create a new one using the button below."
            >
                <template #action>
                    <button
                        class="btn btn-secondary gap-2 flex text-slate-800"
                        @click="openTripModal()"
                    >
                        <Icon icon="plus" />
                        Create a trip
                    </button>
                </template>
            </EmptyState>
        </div>

        <div class="flex flex-col gap-4" v-if="tripScheduled === 'past'">
            <TripCard v-for="trip in pastTrips" :key="trip.id" :trip="trip" />
            <div class="w-full pt-5">
                <Pagination
                    :current-page="pastTripsPage"
                    :total-pages="pastTripsPages"
                    :per-page="perPage"
                    :total-results="pastTripsTotal"
                    :max-visible-buttons="5"
                    @page-changed="handlePastPageChange"
                    v-if="pastTrips.length > 0"
                />
            </div>
            <EmptyState
                v-if="pastTrips.length === 0"
                class="mb-10"
                :icon="'trip'"
                title="No past trips yet"
                description="As soon as your trips finish, you will see them here."
            >
                <template #action>
                    <button class="btn btn-secondary" @click="openTripModal()">
                        &plus; Create a trip
                    </button>
                </template>
            </EmptyState>
        </div>

        <ManageTrip />
        <CreateRedemption v-if="showRedemptionCreateModal" />
    </div>
    <LoadingState :loading="loading" />
</template>
<script setup>
import { ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import { useTripsStore } from '@/store/trips';
import TripCard from '@/components/trips/trip-card.vue';
import ManageTrip from '@/components/trips/modals/manage-trip.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import Pagination from '@/components/ui/pagination/pagination.vue';
import CreateRedemption from '@/components/trips/modals/create-redemption.vue';

const tripsStore = useTripsStore();
const appStore = useAppStore();
const {
    loading,
    showEmptyTripState,
    upcomingTrips,
    pastTrips,
    perPage,
    upcomingTripsPage,
    pastTripsPage,
    upcomingTripsPages,
    pastTripsPages,
    upcomingTripsTotal,
    pastTripsTotal,
    showRedemptionCreateModal,
} = storeToRefs(tripsStore);

const tripScheduled = ref('upcoming');

const handleUpcomingPageChange = (page) => {
    tripsStore.tripsPageChange(page, 'upcoming');
};

const handlePastPageChange = (page) => {
    tripsStore.tripsPageChange(page, 'past');
};

const openTripModal = () => {
    document.activeElement?.blur();
    tripsStore.resetActiveTripData();
    tripsStore.setShowTripModal(true);
};

onMounted(() => {
    tripsStore.getTrips();
});
</script>
